import { useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useFormSelectedErrorFlag } from '@atlassian/jira-proforma-template-library/src/common/utils/form-selected-failed-flag/index.tsx';
import { BLANK_TEMPLATE } from '@atlassian/jira-proforma-template-library/src/services/use-template-index-service/constants.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { TemplateForm } from '@atlassian/proforma-common-core/form-system-models';
import { type CreateFormResponse, createForm } from './utils.tsx';

type RequestObject = {
	templateId: string;
	template: TemplateForm;
	formTemplateKey?: string;
};
export const useCreateForm = (projectId: number) => {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFormSelectFailFlag } = useFormSelectedErrorFlag();

	const requestOld = useCallback(
		async ({ templateId, template }: RequestObject) => {
			const isBlankTemplate = templateId === BLANK_TEMPLATE.id;
			try {
				const createdFormResponse = await createForm(cloudId, projectId, template);
				if (isBlankTemplate) {
					fireTrackAnalytics(createAnalyticsEvent({}), 'blankTemplateForm created', {
						projectId,
						templateFormId: createdFormResponse.form.id,
					});
				} else {
					fireTrackAnalytics(createAnalyticsEvent({}), 'templateFormFromTemplate created', {
						projectId,
						templateId,
						templateFormId: createdFormResponse.form.id,
					});
				}
				return createdFormResponse;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (isBlankTemplate) {
					fireErrorAnalytics({
						meta: {
							id: 'createBlankTemplateForm',
							packageName: 'jiraProformaProjectFormsSettings',
							teamName: 'jsm-request-intake',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
						attributes: {
							projectId,
						},
					});
				} else {
					fireErrorAnalytics({
						meta: {
							id: 'createFormFromTemplate',
							packageName: 'jiraProformaProjectFormsSettings',
							teamName: 'jsm-request-intake',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
						attributes: {
							projectId,
							templateId,
						},
					});
				}
				showFormSelectFailFlag();
				throw error;
			}
		},
		[cloudId, createAnalyticsEvent, projectId, showFormSelectFailFlag],
	);

	const request = useCallback(
		async ({ templateId, template, formTemplateKey }: RequestObject) => {
			const isBlankTemplate = templateId === BLANK_TEMPLATE.id;
			try {
				const createdFormResponse = await createForm(cloudId, projectId, template);
				if (isBlankTemplate) {
					fireTrackAnalytics(createAnalyticsEvent({}), 'blankTemplateForm created', {
						projectId,
						templateFormId: createdFormResponse.form.id,
					});
				} else {
					fireTrackAnalytics(createAnalyticsEvent({}), 'templateFormFromTemplate created', {
						projectId,
						templateFormId: createdFormResponse.form.id,
						formTemplateId: templateId,
						formTemplateKey,
					});
				}
				return createdFormResponse;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (isBlankTemplate) {
					fireErrorAnalytics({
						meta: {
							id: 'createBlankTemplateForm',
							packageName: 'jiraProformaProjectFormsSettings',
							teamName: 'jsm-request-intake',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
						attributes: {
							projectId,
						},
					});
				} else {
					fireErrorAnalytics({
						meta: {
							id: 'createFormFromTemplate',
							packageName: 'jiraProformaProjectFormsSettings',
							teamName: 'jsm-request-intake',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
						attributes: {
							projectId,
							templateId,
						},
					});
				}
				showFormSelectFailFlag();
				throw error;
			}
		},
		[cloudId, createAnalyticsEvent, projectId, showFormSelectFailFlag],
	);

	const { loading, data, error, fetch } = useService<CreateFormResponse, RequestObject>(
		fg('start-1993_change_granular_template_events') ? request : requestOld,
		{
			loading: true,
		},
	);

	return {
		loading,
		data,
		error,
		callCreateForm: fetch,
	};
};
